import { ChangeDetectionStrategy, Component, Inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { getTrackByIndexFn } from '@y42/angular/util';
import { ColumnTypeEnum } from '@y42/json-schema';
import { showLoaderFor } from '@y42/util/show-loader-for-x-ms';
import { BehaviorSubject, merge, of } from 'rxjs';
import { FunctionEditorComponent } from '../../function-editor.component';
import { DatasourceColumnModelMeta, sourceColumnConstants } from '../../helpers/function-string.constant';
import { FunctionListViewComponent } from '../function-list-view/function-list-view.component';

export interface DocumentationModalDialogData {
  lowCodeString: string | null;
  fieldName: string;
  outputType: ColumnTypeEnum;
  existingCalculatedFieldNames: string[];
  columns: DatasourceColumnModelMeta[];
  usedLowCodeStrings: Map<string, { lowCodeString: string; outputType: ColumnTypeEnum }>;
}

@UntilDestroy()
@Component({
  selector: 'y42-documentation-modal',
  templateUrl: './documentation-modal.component.html',
  styleUrls: ['./documentation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentationModalComponent {
  constructor(
    public dialogRef: MatDialogRef<DocumentationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentationModalDialogData,
  ) {
    this.columnConsts = sourceColumnConstants(this.data.columns);
  }

  readonly trackByIndex = getTrackByIndexFn<[string, { lowCodeString: string; outputType: ColumnTypeEnum }]>();

  public readonly usedLowCodeStrings = this.data.usedLowCodeStrings;

  public readonly usedLowCodeStringsEntries = this.usedLowCodeStrings.entries();

  public readonly allowedColumnPattern = '^[a-z][a-z0-9_]*$';
  columnConsts = '';

  public readonly loading$ = merge(of(true), of(false)).pipe(showLoaderFor(500));

  public readonly form = new FormGroup({
    lowCodeString: new FormControl<string>(this.data.lowCodeString || '', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    outputType: new FormControl<ColumnTypeEnum>(this.data.outputType || ColumnTypeEnum.Numeric, {
      validators: [Validators.required],
      nonNullable: true,
    }),
    fieldName: new FormControl<string>(
      this.data.fieldName === 'calculated_field' && !this.data.lowCodeString ? '' : this.data.fieldName,
      { validators: [Validators.required, this.checkForExistingColumns.bind(this)], nonNullable: true },
    ),
  });

  @ViewChild('functionListView') functionListView?: FunctionListViewComponent;
  @ViewChild('functionEditor') functionEditor?: FunctionEditorComponent;

  readonly isValidEditor$ = new BehaviorSubject<boolean>(false);

  openFunctionAgGrid(function_name: string): void {
    setTimeout(() => {
      this.functionListView?.expandFunction(function_name);
    }, 300);
  }

  validateEditor(validity: boolean): void {
    this.isValidEditor$.next(validity);
  }

  async submit() {
    this.dialogRef.close({
      lowCodeString: this.form.value.lowCodeString,
      fieldName: this.form.value.fieldName,
      outputType: this.form.value.outputType,
    });
  }

  addFunctionInEditor(function_name: string): void {
    this.functionEditor?.useFunction(function_name);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  setFunctionEditor(value: string) {
    this.form.controls.lowCodeString.setValue(value);
  }

  private checkForExistingColumns(formControl: FormControl<string>) {
    if (
      this.data.existingCalculatedFieldNames &&
      formControl.value &&
      this.data.existingCalculatedFieldNames.findIndex((f) => f.toLowerCase() === formControl.value.toLowerCase()) !==
        -1
    ) {
      return { columnExists: true };
    }
    return null;
  }
}
